/* eslint-disable react-hooks/preserve-manual-memoization -- Deliberately narrow deps for viewer-derived state */
import { keyBy } from 'lodash/fp';
import { useContext, useMemo } from 'react';
import { ClassificationByCode } from '../../potree/materials/Classifications';
import { RendererContext } from '../../contexts/RendererContext';
import { PointCloud } from '../../types/graphqlTypes';
import { ClipTask } from '../../potree/defines';

export type HeightFilter = { min: number; max: number };
export type IntensityFilter = HeightFilter;
export type ResolutionFilter = { resolutionLevel: number; resolutionValue: number };
export type ClippingFilter = { nrOfClippingBoxes: number; nrOfClippingClusters: number };
export type AppliedFiltersArray = (
  | { name: 'classifications'; value: (ClassificationByCode & { key: string })[] }
  | { name: 'heightFilter'; value: HeightFilter }
  | { name: 'intensityFilter'; value: IntensityFilter }
  | { name: 'resolutionFilter'; value: ResolutionFilter }
  | { name: 'clippingFilter'; value: ClippingFilter }
)[];
export interface AppliedFilters {
  classifications?: (ClassificationByCode & { key: string })[];
  heightFilter?: HeightFilter;
  intensityFilter?: IntensityFilter;
  resolutionFilter?: ResolutionFilter;
  clippingFilter?: ClippingFilter;
}

export const useAppliedFilters = ({ project }: { project: { pointClouds: Pick<PointCloud, 'availableClasses' | 'id'>[] } }) => {
  const rendererContext = useContext(RendererContext);
  const viewer = rendererContext.viewer;

  const appliedFilters = useMemo(() => {
    const pointCloudsById = keyBy('id', project.pointClouds);
    const appliedFilters = viewer?.scene.pointclouds.reduce((result, pc) => {
      const pcId = pc.identifier ?? '';
      const projectPointCloud = pointCloudsById[pcId];
      const noIntensity = !pc?.material?.initialIntensityMax && !pc?.material?.initialIntensityMin;
      const appliedClippingBoxes = viewer.scene.volumes.filter((volume) => volume.assignedClipTask === ClipTask.SHOW_INSIDE || volume.assignedClipTask === ClipTask.SHOW_OUTSIDE);
      const appliedClippingClusters = viewer.scene.pointClusters.filter((cluster) => cluster.assignedClipTask === ClipTask.SHOW_INSIDE || cluster.assignedClipTask === ClipTask.SHOW_OUTSIDE || !cluster.visible);
      const filters = {
        classifications: !viewer.classificationsInfo.hiddenClassesByPointCloud[pcId]?.length
          ? undefined
          : viewer.classificationsInfo.getClassesForPointCloud({ pointcloudId: pcId }).filter((classification) => classification.visible && projectPointCloud?.availableClasses.includes(classification.code)),

        heightFilter:
          pc.material.heightMin !== undefined &&
          pc.material.heightMax !== undefined &&
          (Math.round(pc.material.initialHeightMin) !== Math.round(pc.material.heightMin) || Math.round(pc.material.initialHeightMax) !== Math.round(pc.material.heightMax))
            ? {
                min: Math.round(pc.material.heightMin),
                max: Math.round(pc.material.heightMax),
              }
            : undefined,
        intensityFilter:
          !noIntensity &&
          pc.material.intensityMin !== undefined &&
          pc.material.intensityMax !== undefined &&
          (Math.round(pc.material.initialIntensityMin) !== Math.round(pc.material.intensityMin) || Math.round(pc.material.initialIntensityMax) !== Math.round(pc.material.intensityMax))
            ? {
                min: Math.round(pc.material.intensityMin),
                max: Math.round(pc.material.intensityMax),
              }
            : undefined,
        resolutionFilter:
          pc.downSamplingLevel !== undefined && pc.downSamplingLevel !== Infinity && pc.pcoGeometry?.scale !== undefined
            ? {
                resolutionLevel: pc.downSamplingLevel,
                resolutionValue: Math.pow(2, 14 - pc.downSamplingLevel) / pc.pcoGeometry.scale,
              }
            : undefined,
        clippingFilter:
          appliedClippingBoxes.length || appliedClippingClusters.length
            ? {
                nrOfClippingBoxes: appliedClippingBoxes.length,
                nrOfClippingClusters: appliedClippingClusters.length,
              }
            : undefined,
      };
      const filtersArray = [
        { name: 'classifications', value: filters.classifications },
        { name: 'heightFilter', value: filters.heightFilter },
        { name: 'intensityFilter', value: filters.intensityFilter },
        { name: 'resolutionFilter', value: filters.resolutionFilter },
        { name: 'clippingFilter', value: filters.clippingFilter },
      ];
      return {
        ...result,
        [pcId]: {
          filtersArray,
          filters,
          hasFilters: filtersArray.some((filter) => filter.value !== undefined),
        },
      };
    }, {}) as { [key: string]: { filtersArray: AppliedFiltersArray; hasFilters: boolean; filters: AppliedFilters } };
    return appliedFilters || [];
  }, [project.pointClouds, viewer?.classificationsInfo, viewer?.scene.pointclouds, viewer?.scene.volumes, viewer?.scene.pointClusters]);

  return appliedFilters;
};
